import React, { useState } from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { SecurityCheckResultDetail, executeSecurityCheck } from 'stores/SecurityCheckStoreAdapter';
import { formatDateTime } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault } from 'utils/Toastify';

type Props = {
  detail: SecurityCheckResultDetail;
  onExecuted: () => void;
};

const resultCodeOptions = [
  { value: 'CONFIRMED', label: '確認済み' },
  { value: 'UNUSED', label: '未使用' },
  { value: 'INITIALIZED', label: '初期化済み' },
  { value: 'BROKEN', label: '故障中' },
  { value: 'DISPOSED', label: '廃棄済み' },
];

const SecurityCheckDetail: React.FC<Props> = ({ detail, onExecuted }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [selectedResultCode, setSelectedResultCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isCompleted = detail.resultCode !== null;
  const isMonthly = detail.securityCheckType === 'MONTHLY';
  const isPcPatch = detail.securityCheckType === 'PC_PATCH';

  const canSubmit = isMonthly ? confirmed : selectedResultCode !== '';

  const handleExecute = () => {
    setSubmitting(true);
    const resultCode = isMonthly ? 'CONFIRMED' : selectedResultCode;
    executeSecurityCheck({
      results: [{ securityCheckResultId: detail.securityCheckResultId, resultCode }],
    })
      .then(() => {
        successDefault('セキュリティチェックを実施しました');
        onExecuted();
      })
      .catch((e: Error) => {
        errorDefault('セキュリティチェックの実施に失敗しました', e.message);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  // 管理番号の表示用
  const managementNumbers = [
    detail.managementNumber1,
    detail.managementNumber2,
    detail.managementNumber3,
  ].filter(Boolean);

  if (isCompleted) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {detail.title}
        </Typography>
        <Stack spacing={1.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CheckCircleIcon color="success" />
            <Typography variant="body2" fontWeight={600} color="success.main">
              実施済み
            </Typography>
          </Stack>
          {isPcPatch && detail.assessmentName && (
            <Typography variant="body2">
              PC名: {detail.assessmentName} {managementNumbers.join(' ')}
            </Typography>
          )}
          {isPcPatch && detail.resultCode && (
            <Typography variant="body2">
              実施結果:{' '}
              {resultCodeOptions.find((o) => o.value === detail.resultCode)?.label ||
                detail.resultCode}
            </Typography>
          )}
          <Typography variant="body2">
            実施日時: {detail.completedAt ? formatDateTime(detail.completedAt) : '-'}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        {detail.title}
      </Typography>

      <Stack spacing={2.5}>
        {/* 確認事項 */}
        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            確認事項:
          </Typography>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              p: 2,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
              {detail.content}
            </Typography>
          </Box>
        </Box>

        {/* PC情報 */}
        {isPcPatch && detail.assessmentName && (
          <Box>
            <Typography variant="body2">
              PC名: {detail.assessmentName} {managementNumbers.join(' ')}
            </Typography>
          </Box>
        )}

        {/* 月次: 確認チェックボックス */}
        {isMonthly && (
          <FormControlLabel
            control={
              <Checkbox checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
            }
            label={<Typography variant="body2">上記を確認しました</Typography>}
          />
        )}

        {/* PC: 結果コード選択 */}
        {isPcPatch && (
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              実施結果 *
            </Typography>
            <Select
              value={selectedResultCode}
              onChange={(e) => setSelectedResultCode(e.target.value)}
              displayEmpty
              size="small"
              sx={{ minWidth: 240, borderRadius: 2 }}
            >
              <MenuItem value="" disabled>
                選択してください
              </MenuItem>
              {resultCodeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* 確認ボタン */}
        <Box>
          <Button
            variant="contained"
            disabled={!canSubmit || submitting}
            onClick={handleExecute}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            確認
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default SecurityCheckDetail;
